import clsx from 'clsx';
import React from 'react';
import {
  Callout,
  EmptyState,
  IconReceipt,
  ListPage,
  Pagination,
  Select,
  Spinner,
  StatusPill,
  Table,
  TableSkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../ui/v2';
import { TableSearch } from './TableSearch';
import {
  ADMIN_ORDER_STATUS_OPTIONS,
  ADMIN_ORDERS_PAGE_SIZE,
  adminOrdersCountLabel,
  adminOrderStatusTone,
  formatAmountByCurrency,
  formatLastWebhook,
  formatOrderCreatedAt,
  orderCurrency,
  orderItemsLabel,
  orderPaymentIdLabel,
  orderPosLabel,
  orderStatusLabel,
  orderStoreLabel,
} from './adminOrdersModel';
import type { AdminOrderSortKey } from './platformAdminOrdering';
import type { AdminOrdersState } from './useAdminOrders';

const COLUMNS: {
  key: AdminOrderSortKey;
  label: string;
  align?: 'left' | 'right';
  sortable?: boolean;
}[] = [
  { key: 'id', label: 'ID', sortable: true },
  { key: 'store', label: 'Store', sortable: true },
  { key: 'customer', label: 'Customer', sortable: true },
  { key: 'items', label: 'Items' },
  { key: 'currency', label: 'Currency', sortable: true },
  { key: 'total', label: 'Total', align: 'right', sortable: true },
  { key: 'tax', label: 'Tax', align: 'right', sortable: true },
  { key: 'appFee', label: 'App Fee', align: 'right', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'paid', label: 'Paid', sortable: true },
  { key: 'pos', label: 'POS' },
  { key: 'externalId', label: 'External ID', sortable: true },
  { key: 'paymentId', label: 'Payment ID', sortable: true },
  { key: 'lastWebhook', label: 'Last Webhook', sortable: true },
  { key: 'createdAt', label: 'Created', sortable: true },
];

const Placeholder: React.FC<{ value: React.ReactNode }> = ({ value }) =>
  value === '—' ? <span className="text-v2-fg-3">—</span> : <>{value}</>;

const AdminOrdersTab: React.FC<AdminOrdersState> = ({
  orders,
  loading,
  page,
  totalPages,
  count,
  status,
  search,
  appliedSearch,
  listError,
  sort,
  setPage,
  setStatus,
  onSearchChange,
  toggleSort,
}) => {
  const showInitialLoading = loading && orders.length === 0;
  const isRefreshing = loading && orders.length > 0;
  const filtering = appliedSearch.length > 0;

  return (
    <ListPage
      title="Orders (Admin)"
      meta={adminOrdersCountLabel(count)}
      actions={
        isRefreshing && (
          <span
            role="status"
            className="inline-flex items-center gap-1.5 text-v2-12 text-v2-fg-3"
          >
            <Spinner />
            Updating orders...
          </span>
        )
      }
      toolbar={
        <div className="flex min-h-11 flex-wrap items-center gap-3 px-4 py-2 lg:px-6">
          <TableSearch
            label="Search orders"
            placeholder="Search store, customer, order id, or payment…"
            value={search}
            onChange={onSearchChange}
          />
          <Select
            size="sm"
            aria-label="Order status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-44"
          >
            {ADMIN_ORDER_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
      }
    >
      {listError && (
        <div className="px-4 pt-4 lg:px-6">
          <Callout tone="danger">{listError}</Callout>
        </div>
      )}

      {!showInitialLoading && orders.length === 0 ? (
        <EmptyState
          icon={<IconReceipt />}
          title={
            filtering
              ? 'No orders match that search'
              : "You don't have any orders at the moment"
          }
          description={
            filtering
              ? 'Try a store, customer, order id, or payment id.'
              : "When orders appear, they'll show up here."
          }
        />
      ) : (
        <>
          {showInitialLoading && (
            <p role="status" className="sr-only">
              Loading orders…
            </p>
          )}
          <div
            className={clsx(
              'v2-scroll overflow-x-auto transition-opacity',
              isRefreshing && 'opacity-70',
            )}
          >
            <Table className="min-w-[1400px]">
              <THead>
                <tr>
                  {COLUMNS.map((column) => (
                    <TH
                      key={column.key}
                      align={column.align}
                      sortable={column.sortable && !showInitialLoading}
                      active={column.sortable && sort.key === column.key}
                      dir={sort.direction}
                      onSort={() => toggleSort(column.key)}
                    >
                      {column.label}
                    </TH>
                  ))}
                </tr>
              </THead>
              <TBody>
                {showInitialLoading ? (
                  <TableSkeletonRows cols={COLUMNS.length} />
                ) : (
                  orders.map((order: any, idx: number) => {
                    const currency = orderCurrency(order.currency);
                    const statusLabel = orderStatusLabel(order.status);
                    return (
                      <TR key={order.id || idx} size="lg">
                        <TD num size="lg" className="font-medium">
                          {order.id}
                        </TD>
                        <TD size="lg">
                          <span className="block font-medium">
                            <Placeholder value={orderStoreLabel(order)} />
                          </span>
                          {order.store_id && (
                            <span className="v2-num block text-v2-12 text-v2-fg-3">
                              ID: {order.store_id}
                            </span>
                          )}
                        </TD>
                        <TD size="lg">
                          <span className="block font-medium">
                            <Placeholder value={order.customer_name || '—'} />
                          </span>
                          {order.customer_phone && (
                            <span className="v2-num block text-v2-12 text-v2-fg-3">
                              {order.customer_phone}
                            </span>
                          )}
                        </TD>
                        <TD num size="lg" className="text-v2-fg-2">
                          {orderItemsLabel(order)}
                        </TD>
                        <TD size="lg" className="whitespace-nowrap">
                          {currency.flag && (
                            <span className="mr-1">{currency.flag}</span>
                          )}
                          <Placeholder value={currency.code || '—'} />
                        </TD>
                        <TD num size="lg" align="right" className="font-medium">
                          {formatAmountByCurrency(
                            order.total_price,
                            order.currency,
                          )}
                        </TD>
                        <TD num size="lg" align="right">
                          <Placeholder
                            value={
                              order.tax != null
                                ? formatAmountByCurrency(
                                    order.tax,
                                    order.currency,
                                  )
                                : '—'
                            }
                          />
                        </TD>
                        <TD num size="lg" align="right">
                          <Placeholder
                            value={
                              order.application_fee_cents != null
                                ? formatAmountByCurrency(
                                    order.application_fee_cents,
                                    order.currency,
                                  )
                                : '—'
                            }
                          />
                        </TD>
                        <TD size="lg">
                          {statusLabel ? (
                            <StatusPill
                              tone={adminOrderStatusTone(order.status)}
                            >
                              {statusLabel}
                            </StatusPill>
                          ) : (
                            <Placeholder value="—" />
                          )}
                        </TD>
                        <TD size="lg">
                          <StatusPill
                            tone={order.is_paid ? 'success' : 'neutral'}
                          >
                            {order.is_paid ? 'Yes' : 'No'}
                          </StatusPill>
                        </TD>
                        <TD size="lg">
                          <Placeholder value={orderPosLabel(order)} />
                        </TD>
                        <TD num size="lg" className="whitespace-nowrap">
                          <Placeholder value={order.external_order_id || '—'} />
                        </TD>
                        <TD num size="lg" className="whitespace-nowrap">
                          <Placeholder value={orderPaymentIdLabel(order)} />
                        </TD>
                        <TD
                          size="lg"
                          className="whitespace-nowrap text-v2-fg-2"
                        >
                          <Placeholder
                            value={formatLastWebhook(order.last_webhook)}
                          />
                        </TD>
                        <TD num size="lg" className="whitespace-nowrap">
                          <Placeholder
                            value={formatOrderCreatedAt(order.created_at)}
                          />
                        </TD>
                      </TR>
                    );
                  })
                )}
              </TBody>
            </Table>
          </div>
        </>
      )}

      {totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={count}
          pageSize={ADMIN_ORDERS_PAGE_SIZE}
          onPage={setPage}
          noun="orders"
        />
      )}
    </ListPage>
  );
};

export default AdminOrdersTab;
